import api from 'services/api';
import { array, enforce, nullable, numeric, object, oneOf, runSchema, str } from 'validation';

import { filterConfigSchema, listColumnConfigSchema, type FilterConfig, type ListColumnConfig } from 'api/shared/listConfig';

// GET /event_instances/history — the Event History table. Backend:
// modules/generic_functions/events/getEventInstanceHistory.js UNIONs
// event_instances and file_event_instances into one shape.

export const HISTORY_KINDS = ['process', 'file'] as const;
export type HistoryKind = (typeof HISTORY_KINDS)[number];

/**
 * Three outcomes, not two.
 *
 * A clearer row records either the node instance that consumed the occurrence
 * or the user who discarded it, but a large share of older rows carry no
 * clearer at all, and the backend can still produce one. `unattributed` names
 * that honestly instead of asserting a user action that never happened.
 *
 * Not called `cleared`: in backend terms all three ARE cleared (`raised` false,
 * `cleared_at` set, `POST .../clear` for each), so that word names the umbrella
 * state and cannot also name one bucket inside it.
 */
export const HISTORY_OUTCOMES = ['consumed', 'discarded', 'unattributed'] as const;
export type HistoryOutcome = (typeof HISTORY_OUTCOMES)[number];

export interface EventInstanceHistoryRow {
  readonly kind: HistoryKind;
  /** `event_instance_id` or `file_event_instance_id`; unique only within a kind. */
  readonly occurrence_id: number;
  readonly event_id: number;
  readonly event_name: string;
  readonly event_type: string;
  readonly zone_id: number;
  readonly zone_name: string;
  readonly created_at: string;
  /**
   * Null means unknown, NOT "file kind". File instances record no consumption
   * timestamp at all, and process rows cleared before the column existed have
   * none either — so this must never be used to infer `kind`.
   */
  readonly finished_at: string | null;
  readonly outcome: HistoryOutcome;
  /** The merged kind-specific column: raise comment for process, path for file. */
  readonly description: string | null;
  /** Consuming node definitions and discarding usernames; empty when unattributed. */
  readonly actors: readonly string[];
}

interface Pagination {
  readonly total_records: number;
  readonly total_pages: number;
  readonly current_page: number;
  readonly limit: number;
  readonly offset: number;
}

export interface EventInstanceHistoryPage {
  readonly instances: readonly EventInstanceHistoryRow[];
  readonly pagination: Pagination;
  readonly config: readonly ListColumnConfig[];
  readonly filtersConfig: readonly FilterConfig[];
}

const rowSchema = object<EventInstanceHistoryRow>({
  kind: oneOf(HISTORY_KINDS, 'process'),
  occurrence_id: numeric(),
  event_id: numeric(),
  event_name: str(),
  event_type: str(),
  zone_id: numeric(),
  zone_name: str(),
  created_at: str(),
  finished_at: nullable(str()),
  outcome: oneOf(HISTORY_OUTCOMES, 'unattributed'),
  description: nullable(str()),
  actors: array(str()),
});

// COUNT-derived totals can arrive as numeric strings from Postgres.
const paginationSchema = object<Pagination>({
  total_records: numeric(),
  total_pages: numeric(),
  current_page: numeric(),
  limit: numeric(),
  offset: numeric(),
});

const pageSchema = object<EventInstanceHistoryPage>({
  instances: array(rowSchema),
  pagination: paginationSchema,
  config: array(listColumnConfigSchema),
  filtersConfig: array(filterConfigSchema),
});

/**
 * Every filter the list-table panel offers, and nothing it does not. A filter
 * the panel shows but the read ignores silently returns the unfiltered list,
 * which is worse than not offering it — `actors` is a text[] and needs a
 * different predicate, so it is a column without a filter.
 */
export interface EventInstanceHistoryParams {
  readonly event_id?: number;
  readonly zone_id?: number;
  readonly kind?: HistoryKind;
  readonly outcome?: HistoryOutcome;
  readonly search?: string;
  readonly event_name?: string;
  readonly zone_name?: string;
  readonly description?: string;
  readonly created_at_from?: string;
  readonly created_at_to?: string;
  readonly finished_at_from?: string;
  readonly finished_at_to?: string;
  readonly sortColumn?: string;
  readonly sortOrder?: 'ASC' | 'DESC';
  readonly page?: number;
  readonly limit?: number;
}

/**
 * GET `/event_instances/history`. Throws on transport error or structural
 * validation failure.
 *
 * A row whose `kind` or `occurrence_id` drifted is dropped — together they are
 * the row's identity, and `kind` also decides how `description` reads. Drifted
 * pagination totals fail the call instead: a table cannot be paged from a
 * number it cannot trust, and defaulting a total to 0 would present a
 * populated page as empty.
 */
export const getEventInstanceHistory = async (params: EventInstanceHistoryParams = {}): Promise<EventInstanceHistoryPage> => {
  const res = await api.get('/event_instances/history', { params });
  const result = runSchema(pageSchema, res.data, 'getEventInstanceHistory');
  if (!result.ok) {
    throw new Error('getEventInstanceHistory: response failed validation');
  }
  return enforce(result.value, result.warnings, ['instances[*].occurrence_id', 'instances[*].kind', 'pagination.total_records', 'pagination.total_pages']);
};
